import { Badge, Select } from '@chopchop-se/ui';
import { useQuery } from '@tanstack/react-query';
import type { Shipment } from '@warehouse/shared';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { formatDate } from '../../../lib/format';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { pickingKeys, shipmentsApi } from '../api';
import { ShipmentStatusBadge } from '../components/StatusBadge';

export function ShipmentsPage(): ReactNode {
  const { t } = useTranslation('pickingShipping');
  const navigate = useNavigate();
  const { state, setState, setFilter } = useListState({ defaultSort: 'createdAt', defaultDir: 'desc' });

  const shipments = useQuery({
    queryKey: pickingKeys.shipments(state),
    queryFn: () => shipmentsApi.list(state),
  });
  useLiveQuery(['shipments', 'receptions'], ['shipments'], { exact: false });

  const columns: DataTableColumn<Shipment>[] = [
    {
      id: 'shipmentNo',
      header: t('shipments.shipmentNo'),
      sortField: 'shipmentNo',
      cell: (row) => <span className="font-mono text-sm">{row.shipmentNo}</span>,
    },
    {
      id: 'order',
      header: t('shipments.order'),
      cell: (row) => <span className="font-mono text-sm">{row.orderNo}</span>,
    },
    { id: 'restaurant', header: t('shipments.restaurant'), cell: (row) => row.restaurantName },
    {
      id: 'deliveryDate',
      header: t('shipments.deliveryDate'),
      sortField: 'deliveryDate',
      cell: (row) => formatDate(row.deliveryDate),
    },
    {
      id: 'pickupDate',
      header: t('shipments.pickupDate'),
      sortField: 'pickupDate',
      hideBelow: 'lg',
      cell: (row) => (row.pickupDate ? formatDate(row.pickupDate) : '—'),
    },
    {
      id: 'type',
      header: t('shipments.type'),
      hideBelow: 'md',
      cell: (row) => (
        <Badge tone={row.type === 'own' ? 'accent' : 'neutral'} size="sm">
          {t(`shipments.types.${row.type}`)}
        </Badge>
      ),
    },
    {
      id: 'status',
      header: t('shipments.status.draft'),
      sortField: 'status',
      cell: (row) => (
        <div className="flex items-center gap-1.5">
          <ShipmentStatusBadge status={row.status} />
          {row.status === 'booked' && row.orderStatus === 'pagaende' ? (
            <Badge tone="info" size="sm">
              {t('shipments.preBooking.badge')}
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      id: 'consignmentNo',
      header: t('shipments.consignmentNo'),
      hideBelow: 'xl',
      cell: (row) => <span className="font-mono text-xs">{row.consignmentNo ?? '—'}</span>,
    },
  ];

  return (
    <>
      <PageHeader title={t('shipments.title')} subtitle={t('shipments.subtitle')} />

      <DataTable
        columns={columns}
        rows={shipments.data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={shipments.data?.total ?? null}
        loading={shipments.isLoading}
        error={shipments.error}
        emptyTitle={t('shipments.empty')}
        emptyDescription={t('shipments.emptyDescription')}
        onRowClick={(row) => {
          void navigate(`/orders/shipments/${row.id}`);
        }}
        filters={
          <>
            <Select
              value={String(state.filters?.status ?? '')}
              placeholder={t('shipments.status.draft')}
              aria-label={t('shipments.status.draft')}
              options={[
                { value: '', label: '—' },
                { value: 'draft', label: t('shipments.status.draft') },
                { value: 'booked', label: t('shipments.status.booked') },
                { value: 'shipped', label: t('shipments.status.shipped') },
                { value: 'delivered', label: t('shipments.status.delivered') },
                { value: 'cancelled', label: t('shipments.status.cancelled') },
              ]}
              onChange={(event) => {
                setFilter('status', event.target.value || undefined);
              }}
            />
            <Select
              value={String(state.filters?.type ?? '')}
              placeholder={t('shipments.type')}
              aria-label={t('shipments.type')}
              options={[
                { value: '', label: '—' },
                { value: 'carrier', label: t('shipments.types.carrier') },
                { value: 'own', label: t('shipments.types.own') },
              ]}
              onChange={(event) => {
                setFilter('type', event.target.value || undefined);
              }}
            />
          </>
        }
      />
    </>
  );
}
